import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Subject, takeUntil, tap } from 'rxjs';
import { FormioEvent } from 'dp-formio';
import {
	EVENTS,
	HooksType,
	SelectCustomOptionsType,
	SelectRequestOptionsType,
	SelectRequestType,
	evaluatedString,
} from './models';
import { CustomSelectService } from './services';

import { NgSelectConfig } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { CustomComponent, Utils } from 'formiojs';

@Component({
	selector: 'cs-custom-select',
	templateUrl: './custom-select.component.html',
	styleUrls: ['./custom-select.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [CustomSelectService],
})
export class CustomSelectComponent implements OnDestroy, OnChanges {
	formioEvent?: EventEmitter<FormioEvent>;
	destroy$ = new Subject<void>();
	@Input() data: Record<string, any>;
	@Input() customOptions: SelectCustomOptionsType;
	@Input() formioComponent: CustomComponent;

	@Input() required: boolean;
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;

	@Input() value: any;
	@Output() valueChange = new EventEmitter();

	private readonly _optionsSub = new BehaviorSubject([]);

	options$ = this._optionsSub.asObservable().pipe(
		tap((data) => {
			this._callHook(EVENTS.afterLoadItems, data);
			this._dispatchEvent(EVENTS.afterLoadItems, data);
		})
	);

	itemTemplate: evaluatedString;
	groupTemplate: evaluatedString;
	selectedTemplate: evaluatedString;
	bindValue: string;
	bindLabel: string;
	groupBy: string;
	bindGroup: string;
	multiple: boolean = false;
	closeOnSelect: boolean = true;
	selectableGroup: boolean = false;
	selectableGroupAsModel: boolean = false;
	dispatchEvents: boolean = false;
	hideSelected: boolean = false;
	hooks: HooksType;
	searchFn: (term: string, item: any) => boolean;
	constructor(
		private selectService: CustomSelectService,
		private _config: NgSelectConfig,
		private translateService: TranslateService
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.customOptions?.currentValue) {
			this._prepareOptions();
			this._prepareData();
		}
	}

	updateValue(value: any) {
		this.value = value;
		this.valueChange.emit(value);
		this._callHook(EVENTS.afterValueChange, value);
		this._dispatchEvent(EVENTS.afterValueChange, value);
	}

	onChange(data: any) {
		this._callHook(EVENTS.onChange, data);
		this._dispatchEvent(EVENTS.onChange, data);
	}

	onAdd(data: any) {
		this._callHook(EVENTS.onAdd, data);
		this._dispatchEvent(EVENTS.onAdd, data);
	}

	onRemove(data: any) {
		this._dispatchEvent(EVENTS.onRemove, data);
		this._dispatchEvent(EVENTS.onRemove, data);
	}

	onSearch(data: { term: string; items: any[] }) {
		this._callHook(EVENTS.onSearch, data);
		this._dispatchEvent(EVENTS.onSearch, data);
	}

	private _prepareOptions() {
		this.itemTemplate = this.customOptions?.itemTemplate;
		this.groupTemplate = this.customOptions?.groupTemplate;
		this.selectedTemplate = this.customOptions?.selectedTemplate;
		this.bindLabel = this.customOptions?.bindLabel;
		this.bindValue = this.customOptions?.bindValue;
		this.bindGroup = this.customOptions?.bindGroup;
		this.groupBy = this.customOptions?.groupBy;
		this.multiple = this.customOptions?.multiple;
		this.selectableGroup = this.customOptions?.selectableGroup;
		this.selectableGroupAsModel = this.customOptions?.selectableGroupAsModel;
		this.dispatchEvents = this.customOptions?.dispatchEvents;
		this.hooks = this.customOptions?.hooks;
		this.hideSelected = this.customOptions?.hideSelected;
		this.searchFn = this._search(this.customOptions?.searchFields);
	}

	private _search(searchFields: string): (term: string, item: any) => boolean {
		const fields = `${searchFields}`.split(',') || [];
		return (term: string, item: any) => {
			if (!fields?.length) {
				return (
					`${item[this.bindLabel]}`.toLowerCase().includes(term.toLowerCase()) ||
					`${item[this.bindValue]}`.toLowerCase().includes(term.toLowerCase())
				);
			}

			return fields.some((field) => `${item?.[field]}`.toLowerCase().includes(term.toLowerCase()));
		};
	}

	private _prepareData() {
		if (this.customOptions.url) {
			this._getDataFromUrl();
			return;
		}
		if (this.customOptions.request && this.customOptions.request?.url) {
			this._getDataFromRequest();
		}
	}

	private _getDataFromRequest() {
		try {
			const url = this._evaluate(this.customOptions.request.url);
			if (url) {
				const requestData = this._prepareRequest(url, this.customOptions.request);

				this.selectService
					.request(requestData)
					.pipe(takeUntil(this.destroy$))
					.subscribe((res) => {
						if (!this.customOptions.request?.onSuccess) {
							this._optionsSub.next(res);
							return;
						}
						const _evaluateData = this._evaluate(this.customOptions.request?.onSuccess, {
							response: res,
						});
						this._optionsSub.next(_evaluateData || res);
					});
			}
		} catch (error) {
			console.log(error);
		}
	}

	private _prepareRequest(url: any, request: SelectRequestOptionsType) {
		const _data = {
			url: url,
			method: request?.method || 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		};

		if (request.params) {
			_data['params'] = this._evaluate(request.params) || {};
		}
		if (request.headers) {
			_data['headers'] = this._evaluate(request.headers) || null;
		}
		if (request.body) {
			_data['body'] = this._evaluate(request.body) || null;
		}

		return _data as SelectRequestType;
	}

	private _getDataFromUrl() {
		try {
			const url = this._interpolate(this.customOptions.url);
			this.selectService
				.get(url)
				.pipe(takeUntil(this.destroy$))
				.subscribe((res) => {
					this._optionsSub.next(res);
				});
		} catch (error) {}
	}

	private _interpolate(string: string, data: Record<string, any> = {}) {
		return Utils.interpolate(string, Object.assign(this._evaluateData(), data));
	}

	private _evaluate(string: string, data: Record<string, any> = {}) {
		return Utils.evaluate(string, Object.assign(this._evaluateData(), data));
	}

	private getFormConfig() {
		const config =
			this.formioComponent.root.form.config ||
			this.formioComponent?.wizard?.config ||
			this.formioComponent.root?.wizard?.config;
		return config;
	}

	private _evaluateData() {
		return {
			config: this.getFormConfig(),
			data: this.data,
			instance: this.formioComponent,
			submission: this.formioComponent.root.submission,
			utils: Utils,
		};
	}

	private _callHook(hook: (typeof EVENTS)[keyof typeof EVENTS], data: unknown) {
		if (this.hooks?.[hook]) {
			this._evaluate(this.hooks[hook], { hookData: data });
		}
	}

	private _dispatchEvent(event: (typeof EVENTS)[keyof typeof EVENTS], data: unknown) {
		if (this.dispatchEvents) {
			this.formioComponent.element.dispatchEvent(
				new CustomEvent(`CUSTOM_SELECT__${event}`, {
					bubbles: true,
					detail: data,
				})
			);
		}
	}
}
